// ECONIC shared front-end contracts (SPEC-ECONIC §4.6, TS list). Owned by WP-F0-WEB and FROZEN after gate G0: a change goes through
// WP-INTEG-QA (BUILD-PLAN §4.3); additive fields are accepted the same day. The engine vocabulary (widgets, layouts, ops, snippets) is
// `../ui.ts`. Types a sandboxed desk page also needs (the desk header, the marking scale, module, desk and report-type keys, provenance)
// are defined once in `@ncp/page-sdk` and re-exported here, so the host and the pages read one definition.
import type { AuthUser } from "@ncp/api-client";
import type { LocalisedText } from "@ncp/page-sdk/types";
import type { PrefKey } from "../ui";

export type {
  DeskChipKind,
  DeskHeaderState,
  DeskHeaderStateKey,
  DeskKey,
  DeskNumber,
  DeskTone,
  KnownReportTypeKey,
  ModuleKey,
  Provenance,
  ReportTypeKey,
  Sensitivity,
  SourceState,
} from "@ncp/page-sdk/types";
import type { ModuleKey, ReportTypeKey, Sensitivity, SourceState, Provenance } from "@ncp/page-sdk/types";

// ------------------------------------------------------------------ text and keys

/** A bilingual text; Arabic falls back to English only where the source has no Arabic (never machine-translated in the UI). */
export type Bi = LocalisedText;

/** The marking scale in display order, lowest first: the banner shows the highest level present on screen. */
export const SENSITIVITY_ORDER: readonly Sensitivity[] = ["public", "internal", "restricted", "confidential"];

/** The six agents in the Ministry's order (16 Oct 2026): Knowledge Bank · Economic Radar · Country360 · Policy (PMO) · Economic Modelling · News & Signals. */
export const MODULE_KEYS: readonly ModuleKey[] = ["kb", "uae-radar", "country360", "policylab", "outlook", "global-radar"];

/**
 * The destinations of the top navigation (16 Oct 2026): ECONIC (`today`: the landing and its conversations) · Agents (`intelligence`: the
 * hub, the six agents and the Knowledge Bank) · My workspace (`workspaces`: the board, the desks, files and pages), plus the Trust Centre
 * and Admin for the DT Administrator. The ids stay as they were; only what they are called changed.
 */
export type Destination = "today" | "intelligence" | "workspaces" | "trust" | "admin";

/** Canonical department keys (SPEC C16); the database tables are the source of truth, seeded from `spaces/_shared/econic/departments.json`. */
export type DepartmentKey =
  | "ministers-office" | "undersecretary-office" | "economic-policies-studies" | "info-economic-studies" | "economic-policies-legislation"
  | "new-economy" | "international-economic-relations" | "tourism" | "intellectual-property" | "smes-entrepreneurship"
  | "commercial-control" | "digital-transformation" | "other-units";

// ------------------------------------------------------------------ module status (GET /api/econic/modules/status; WP-MODKIT)

export interface ModuleSourceHealth {
  key: string;
  name: Bi;
  state: SourceState;
  /** The last time a collector reached the publisher (ISO), or null when it never has. */
  lastSyncAt: string | null;
  live: boolean;
}

/** One module on the rail: its count and whether action is due (the only case it turns red), plus source health. */
export interface ModuleStatus {
  key: ModuleKey;
  state: "live" | "stale" | "awaiting";
  asOf: string | null;
  /** What the rail counts for this module (KB records, open signals, …), or null when nothing is counted yet. */
  count: number | null;
  due: boolean;
  summary: Bi;
  sources: ModuleSourceHealth[];
}

// ------------------------------------------------------------------ departments (GET /api/econic/departments; WP-SHELL)

export interface DepartmentUnit {
  key: string;
  kind: "unit" | "sector" | "department";
  name: Bi;
  short?: Bi;
  initials?: string;
  status: "live" | "onboarding";
  /** The workspace a live department maps to (IES `economic-studies`, EPL `economic-policies`, NE `new-economy`). */
  workspace: string | null;
  kbPortal: string;
  /** The caller may switch to it (only `member && status === "live"` is selectable). */
  member: boolean;
  departments?: DepartmentUnit[];
}

export interface DepartmentsResponse {
  /** In display order; a sector carries `departments`. */
  units: DepartmentUnit[];
  /** Resolved from `pref:ui.department`, then membership, else null. */
  current: string | null;
  /** Memberships not mapped to a department (competition, economic reports, pages lab). */
  otherWorkspaces: { key: string; name: string; nameAr?: string | null; icon: string }[];
}

// ------------------------------------------------------------------ session (GET /api/auth/session; WP-SEC-API, SPEC C12)

export interface SessionInfo {
  sid: string;
  signedInAt: string;
  mfa: boolean;
  amr: string[];
  /** The session before this one (last sign-in), or null on a first sign-in. */
  previous: { at: string; device: string; place?: string } | null;
  idleTimeoutSec: number;
  idleExpiresAt: string;
  /** signedInAt + 10 h; it cannot be extended. */
  absoluteExpiresAt: string;
  otherActiveSessions: number;
}

// ------------------------------------------------------------------ classification banner (econic/shell/classification.tsx; WP-SHELL)

/** One reason the banner is at its level: "Knowledge Bank document ‘…’", "Desk floor", "Cited in this conversation". */
export interface ClassificationSource {
  level: Sensitivity;
  source: string;
}

// ------------------------------------------------------------------ figures (modules-b §0.3 with SPEC C22)

/** The role a figure's source plays: UAE official first, international as comparison (DESIGN-SYSTEM §1.4). */
export type FigRole = "primary" | "comparison" | "forecast" | "model" | "folder" | "kb";

/** Labels that must sit next to a figure of that nature (SPEC §21.3). */
export type EpistemicKind =
  | "nowcast" | "ai-draft" | "options" | "proposed-trigger" | "mirror" | "method-illustration" | "external-model" | "preliminary" | "stale";

/** Where an AI-made output stands; nothing is decided by AI (SPEC §1.12). */
export type AiState = "proposed" | "draft" | "verified" | "approved" | "superseded";

/** A locator behind a figure: a warehouse series, a Knowledge Bank document (with page), a live read or a model run. */
export interface FigRef {
  kind: "series" | "document" | "live" | "model-run";
  key: string;
  label: string;
  /** "p. 5", a table cell, a chunk id. */
  locator?: string;
  url?: string;
}

/**
 * One figure as every surface shows it (one value per fact): the number, its unit and period, the source's role and short chip text, and
 * its provenance (C22: the only freshness model). A figure without provenance renders "—" with "No source".
 */
export interface Fig {
  value: number | null;
  unit?: string;
  decimals?: number;
  period?: string;
  role: FigRole;
  /** Short chip text, e.g. "MoFT · 2025". */
  source: string;
  provenance: Provenance[];
  refs?: FigRef[];
  delta?: { value: number; unit: "%" | "pp" | "abs"; vs: string };
  /** Read from an OCR'd or unconfirmed source: shows the "verify" flag. */
  verify?: boolean;
  epistemic?: EpistemicKind;
}

// ------------------------------------------------------------------ shell bundle (GET /api/econic/shell, GET /api/econic/inbox/summary; WP-SHELL)

export interface ShellCounts {
  decisions: number;
  /** The decision count is a lower bound ("n+"). */
  decisionsAtLeast: boolean;
  waiting: number;
  notifications: number;
  kbReviews: number;
  tasksOverdue: number;
  /** Alerts per workspace key. */
  deskAlerts: Record<string, number>;
}

/** The one boot call of the frame (≤ 80 ms server): who, session, prefs, departments, counts, module status. */
export interface ShellSummary {
  me: AuthUser & { department: string | null; landing: string };
  session: SessionInfo;
  prefs: Partial<Record<PrefKey, unknown>>;
  departments: DepartmentsResponse;
  counts: ShellCounts;
  modules?: ModuleStatus[];
  environment: "Development" | "QA" | "Production";
  version: string;
  announcement?: { level: "info" | "warning"; text: Bi; until: string } | null;
  /**
   * How this host sends text to external models (additive): `zeroRetention` is `Llm:RequireZeroDataRetention` as the route (API.ae or the
   * OpenRouter handler) enforces it; `external` is false when the host runs offline. The frame says "zero retention" only when `zeroRetention` is true.
   */
  ai?: { zeroRetention: boolean; external: boolean } | null;
  /**
   * The Ministry's subscribed sources (additive, 22 Oct 2026): `subscribed` is true once at least one subscription is connected (enabled and
   * tested), and the sources row's "Subscribed" can then be chosen; `providers` names them.
   */
  sources?: { subscribed: boolean; providers: Array<{ key: string; name: string; nameAr: string }> } | null;
}

export type EconicInboxTab = "decisions" | "waiting" | "notifications";

export interface EconicInboxRow {
  id: string;
  tab: EconicInboxTab;
  kind: string;
  title: Bi;
  department?: string | null;
  at: string;
  href: string;
  /** The row's primary action: Review (a decision) or Open. */
  action: "review" | "open";
  unread?: boolean;
}

/**
 * The bell's three tabs, up to `take` rows each, with totals (the server applies who may decide). SPEC §4.6 calls it `InboxSummary`; it is
 * `EconicInboxSummary` here because the SDK already exports the assistant inbox's `InboxSummary` (`types.ts`).
 */
export interface EconicInboxSummary {
  decisions: { total: number; atLeast: boolean; rows: EconicInboxRow[] };
  waiting: { total: number; rows: EconicInboxRow[] };
  notifications: { total: number; unread: number; rows: EconicInboxRow[] };
  /** "7 mine · 23 department". */
  department?: { total: number } | null;
}

// ------------------------------------------------------------------ search (GET /api/econic/search; WP-SHELL)

export type SearchHitKind = "kb" | "indicator" | "chat" | "file" | "person";

export interface SearchHit {
  id: string;
  kind: SearchHitKind;
  title: string;
  titleAr?: string | null;
  /** Portal · owner role · date | series · unit | case number. */
  subtitle?: string | null;
  /** ≤ 160 characters; `<mark>` only, escaped by the server. Restricted documents carry none (title and locator only). */
  snippet?: string | null;
  href: string;
  sensitivity?: Sensitivity;
  department?: string | null;
  score: number;
}

export interface SearchResultGroup {
  kind: SearchHitKind;
  items: SearchHit[];
  total?: number;
}

export interface SearchResponse {
  q: string;
  tookMs: number;
  /** Sources that missed their budget and were dropped. */
  partial: string[];
  groups: SearchResultGroup[];
}

// ------------------------------------------------------------------ reports (reports.draft; WP-PDF-API)

/** What `reports.draft` returns to its caller: the draft's artifact and revision, opened at `/reports/:artifactId`. */
export interface ReportDraftRef {
  artifactId: string;
  type: ReportTypeKey;
  revision: number;
  locale: "en" | "ar" | "both";
  reference?: string | null;
  classification?: Sensitivity;
}

/** The report studio route of a draft. */
export const reportHref = (ref: Pick<ReportDraftRef, "artifactId">): string => `/reports/${encodeURIComponent(ref.artifactId)}`;
